import { FirestoreDoc } from "@p0security/frontend-core/react";
import { integrationRegistry } from "@p0security/shared/integrations/registry";
import { toRequestAttribution } from "@p0security/shared/permission-requests/attribution";
import {
  AccessRequestOf,
  isAccessRequestOf,
} from "@p0security/shared/types/access/index";
import { Evidence } from "@p0security/shared/types/evidence";
import { assertNever } from "@p0security/shared/types/index";
import type { EmptyAccesses } from "@p0security/shared/types/lifecycle";
import {
  Permission,
  PermissionRequest,
  RequestNotificationKey,
  RequestNotificationValue,
  RequestNotifications,
} from "@p0security/shared/types/permission";
import { isPermissionType } from "@p0security/shared/types/permission";
import {
  HandledRequestStatuses,
  RequestStatus,
  RequestStatuses,
  TerminalRequestStatuses,
} from "@p0security/shared/types/request-status";
import { message } from "@p0security/ui";
import { widetype } from "@p0security/util/collections";
import { isa } from "@p0security/util/types/is";
import {
  cloneDeep,
  compact,
  get,
  isArray,
  lowerFirst,
  startCase,
} from "lodash";
import pluralize from "pluralize";
import { colors } from "styles/variables";

import {
  directories,
  resourceIntegrations,
} from "../../Integrations/constants.tsx";
import { renderPreApproval } from "./requestRenderUtils";
import type { RequestAttribution } from "./types.ts";

const SLACK_TLD = "slack.com";
const MICROSOFT_TLD = "microsoft.com";

/** Rendered when a value can not otherwise be determined */
const UNKNOWN = "(unknown)";

/** Maximum characters for description rendering */
const MAX_CHARS = 32;

type DescriptionOptions = {
  /** Cuts string values at {@link MAX_CHARS}; defaults to `true` */
  truncate?: boolean;
};

export const VALID_MESSAGE_TLD = [SLACK_TLD, MICROSOFT_TLD];

const noNotifiersConfiguredMessage = (notificationType: string) =>
  `${notificationType} notification not sent, no notifiers configured`;

export const COLOR_MAP: {
  [k: string]: { bg: string; text: string };
} = {
  "Pending Approval": colors.tagColors.blue,
  "Request Created": colors.tagColors.blue,
  Approved: colors.tagColors.blue,
  Staged: colors.tagColors.blue,
  Denied: colors.tagColors.orange,
  Errored: colors.tagColors.red,
  Expired: colors.tagColors.grey,
  Expiring: colors.tagColors.grey,
  Granted: colors.tagColors.blue,
  "N/A": colors.tagColors.grey,
  Relinquished: colors.tagColors.grey,
  Relinquishing: colors.tagColors.grey,
  Revoked: colors.tagColors.grey,
  Revoking: colors.tagColors.grey,
  "Timed Out": colors.tagColors.orange,
} as const;

export const requestLocator = (request: Permission) => {
  if (!isPermissionType(request.type)) return null;
  if (!isAccessRequestOf(request.type, request)) return null;
  const registration = integrationRegistry[request.type];
  return accessLocator(registration.accesses, request);
};

/** Renders a request's grant
 *
 * Pass `{ truncate: false }` where the whole grant must be read, e.g. before
 * approving.
 */
export const requestDescription = (
  request: Permission,
  options: DescriptionOptions = {}
): string => {
  try {
    if (!isPermissionType(request.type)) return UNKNOWN;
    if (!isAccessRequestOf(request.type, request)) return UNKNOWN;
    const registration = integrationRegistry[request.type];
    return accessDescription(registration.accesses, request, options);
  } catch (error) {
    console.error("Error building request description", {
      request,
      error,
    });
  }
  return UNKNOWN;
};

const objectToDescription = (value: object) =>
  value === null
    ? ""
    : Object.getOwnPropertyNames(value)
        .map((n) => `${n}=${String(get(value, n))}`)
        .join(",");

export const accessLocator = <Accesses extends EmptyAccesses>(
  accesses: Accesses,
  request: AccessRequestOf<Accesses, keyof Accesses & string>
): string | undefined => {
  const access = accesses[request.access];
  if (!access || !access.render.locator) return undefined;
  const value = request.permission[access.render.locator];
  if (typeof value === "object") return objectToDescription(value);
  if (typeof value !== "string") return undefined;
  return value;
};

const parameterDescription = (
  label: string,
  value: string[] | boolean | object | string,
  { truncate = true }: DescriptionOptions
): string | undefined => {
  if (typeof value === "boolean") {
    return value ? label : `No ${lowerFirst(label)}`;
  }
  if (typeof value === "string") {
    return truncate && value.length > MAX_CHARS
      ? `${value.slice(0, MAX_CHARS)}…`
      : value;
  }
  if (isArray(value)) {
    return value.length === 0
      ? "(none)"
      : value.length === 1
        ? value[0]
        : `${value[0]} + ${value.length - 1} other(s)`;
  }
  if (typeof value === "object") {
    return objectToDescription(value);
  }
  return undefined;
};

export const accessDescription = <Accesses extends EmptyAccesses>(
  accesses: Accesses,
  request: AccessRequestOf<Accesses, keyof Accesses & string>,
  options: DescriptionOptions = {}
) => {
  const access = accesses[request.access];
  if (!access) return UNKNOWN;
  return compact([
    access.label,
    access.render.policy &&
      parameterDescription(
        access.parameters[access.render.policy].label,
        request.permission[access.render.policy],
        options
      ),
    access.render.resource &&
      parameterDescription(
        access.parameters[access.render.resource].label,
        request.permission[access.render.resource],
        options
      ),
  ]).join(" | ");
};

const friendlyPrincipal = (request: PermissionRequest) => request.principal;

export type RequestLog = {
  initialStatus: RequestStatus;
  updates?: PermissionRequest;
};

export const progressMessages: Record<RequestStatus, string> = {
  NEW: "Setting up policy",
  PENDING_APPROVAL: "Policy initiated; waiting for approval",
  PENDING_APPROVAL_ESCALATED:
    "Policy initiated with escalated notification; waiting for approval",
  APPROVED: "Approved; notifying requestor",
  APPROVED_NOTIFIED: "Staging access",
  ERRORED: "Errored; notifying requestor",
  ERRORED_NOTIFIED: "N/A",
  REVOKE_SUBMITTED: "Revoking request",
  DENIED: "Denied; notifying requestor",
  DENIED_NOTIFIED: "N/A",
  DONE: "Access granted; notifying requestor",
  DONE_NOTIFIED: "Access granted; awaiting expiry",
  DRAFT: "N/A",
  EXPIRY_SUBMITTED: "Expiring request",
  EXPIRED: "Access expired; notifying requestor",
  EXPIRED_NOTIFIED: "N/A",
  REVOKED: "Revoking request; notifying requestor",
  REVOKED_NOTIFIED: "N/A",
  TRANSLATED: "N/A",
  ERRORED_ERRORED: "N/A",
  STAGED: "Granting access",
  TIMED_OUT: "Timed out waiting for approval",
  TIMED_OUT_NOTIFIED: "N/A",
  CLEANUP_SUBMITTED: "Cleanup in progress",
  CLEANED_UP: "Cleanup completed",
  CLEANUP_ERRORED: "Cleanup encountered an error",
};

export const extractApproverName = (
  request: PermissionRequest,
  richPreApproval: boolean
) => {
  // check approvalDetails first, then approverName, then slack notification
  // in approvalDetails
  const { approvalDetails: details, notifications } = request;
  return details
    ? details.approvalSource === "persistent"
      ? "Persistent access"
      : details.approvalSource === "evidence"
        ? renderPreApproval(getPreApprovalData(request), richPreApproval)
        : (details.email ?? details.name ?? details.id ?? UNKNOWN)
    : // Old Request, will be migrated to approvalDetails
      ((request as any).approverName ??
        (notifications?.slack as any)?.approvalByEmail ??
        (request.status === "NEW" || request.status === "PENDING_APPROVAL"
          ? "N/A"
          : UNKNOWN));
};

/** Renders a request's acting identity together with any agentic or
 * on-behalf-of attribution, mirroring the notifier renderers (see
 * `buildRequestInfo`), e.g. `alice@ex.com (on behalf of bob@ex.com, via agent
 * cursor)` or `Agent cursor` */
export const requestorAttribution = (
  request: Pick<PermissionRequest, "agentic" | "principal" | "requestor">
): RequestAttribution[] => {
  const { onBehalfOf, requestor, viaAgents } = toRequestAttribution(request);
  const principal: RequestAttribution = {
    type: requestor.type,
    sub: requestor.sub,
    role: "principal",
  };
  const attribution: RequestAttribution[] = compact([
    onBehalfOf ? { type: "user", sub: onBehalfOf, role: "proxy" } : null,
    viaAgents.length
      ? { type: "agent", sub: viaAgents.at(0) || "", role: "delegate" }
      : null,
  ]);
  return [principal, ...attribution];
};

export const statusDescriptions: Record<
  RequestStatus,
  (request: PermissionRequest) => string | undefined
> = {
  EXPIRED: (request) => `
        Access expired; ${requestDescription(request)} removed from${" "}
        ${friendlyPrincipal(request)}
     `,
  DRAFT: () => "Not yet created",
  NEW: () => "Created request",
  PENDING_APPROVAL: (request) => `
        Sent request for approval to ${renderNotificationApprovalLinks(request)}
      `,
  PENDING_APPROVAL_ESCALATED: (request) => `
        Escalated request for approval
        ${extractEscalationDetails(request) ?? ""}
      `,
  // APPROVED and DENIED must use richOutput = false as statusDescriptions is used both in the browser and in the export
  // files, where they will render as "[object Object]" if richOutput is true. Unfortunately, this means that the
  // timeline view in a request's details will show the plain text as well instead of the rich evidence link. A link
  // will be displayed in the tabular view in a request's details though, as well as in the request history table.
  APPROVED: (request) =>
    `Request approved by ${extractApproverName(request, false)}`,
  DENIED: (request) =>
    `Request denied by ${extractApproverName(request, false)}`,
  REVOKED: (request) => `Access revoked; ${requestDescription(
    request
  )} removed from${" "}
        ${friendlyPrincipal(request)}`,
  STAGED: () => `Staged request`,
  DONE: (request) => `Granted ${requestDescription(request)} to
        ${friendlyPrincipal(request)}`,
  APPROVED_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this request was approved`
      : noNotifiersConfiguredMessage("Approval"),
  DENIED_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this request was denied`
      : noNotifiersConfiguredMessage("Deny"),
  EXPIRED_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this access has expired`
      : noNotifiersConfiguredMessage("Expiration"),
  ERRORED_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this request encountered an error`
      : noNotifiersConfiguredMessage("Error"),
  REVOKED_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this access was revoked`
      : noNotifiersConfiguredMessage("Revoke"),
  DONE_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this access was granted`
      : noNotifiersConfiguredMessage("Grant"),
  ERRORED_ERRORED: () => `
        Encountered an irrecoverable error while processing another error;
        contact support@p0.dev for assistance
      `,
  ERRORED: (request) => `
        Encountered an error while trying to process this access request:
        ${request.error?.message}
      `,
  EXPIRY_SUBMITTED: () =>
    "Triggered access expiration (principal still has access at this time)",
  REVOKE_SUBMITTED: (request) =>
    request.revocationDetails?.email &&
    request.revocationDetails.email !== request.principal
      ? `${request.revocationDetails.email} revoked ${requestDescription(request)} from ${friendlyPrincipal(request)}`
      : `${request.principal} relinquished ${requestDescription(request)}`,
  TRANSLATED: () => "N/A",
  TIMED_OUT: () => "Timed out waiting for approval",
  TIMED_OUT_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this request timed out`
      : noNotifiersConfiguredMessage("Timeout"),
  CLEANUP_SUBMITTED: () => "Cleaning up grants that should not be present",
  CLEANED_UP: () => "Cleanup completed",
  CLEANUP_ERRORED: () => "Cleanup encountered an error",
};

type ApprovalLink = { url?: string; label: string };

/** Shared by every notifier whose approval link is a conversation URL
 * (currently slack and msTeams). */
const conversationUrlApprovalLink = (
  label: string,
  value: RequestNotificationValue
): ApprovalLink | undefined =>
  value && "approvalConversationUrl" in value && value.approvalConversationUrl
    ? { url: value.approvalConversationUrl, label }
    : undefined;

/** One resolver per known notifier key, dispatched dynamically by
 * {@link getNotificationApprovalLinks}. A key with no entry simply
 * contributes no link. */
const notificationApprovalLinkResolvers: Record<
  RequestNotificationKey,
  (value: RequestNotificationValue) => ApprovalLink | undefined
> = {
  slack: (value) => conversationUrlApprovalLink("Slack", value),
  msTeams: (value) => conversationUrlApprovalLink("Microsoft Teams", value),
  email: (value) =>
    value && "approvalEmailData" in value && value.approvalEmailData
      ? { label: "Email" }
      : undefined,
};

const isRequestNotificationKey = (key: string): key is RequestNotificationKey =>
  Object.hasOwn(notificationApprovalLinkResolvers, key);

export const getNotificationApprovalLinks = (
  notifications?: RequestNotifications
): ApprovalLink[] => {
  if (!notifications) return [];

  const links = widetype
    .entries(notifications)
    .map(([key, value]) =>
      isRequestNotificationKey(key)
        ? notificationApprovalLinkResolvers[key](value)
        : undefined
    );
  return compact(links);
};

const renderNotificationApprovalLinks = (request: PermissionRequest) =>
  getNotificationApprovalLinks(request.notifications)
    .map((link) => link.label)
    .join(", ");

// Helper function for reconstructing request history. Given a status and list of
// request log documents, returns the log for the next logged status, and adds any intermediate
// events that occurred between the two logs
const findNextLog = (
  updates: PermissionRequest,
  logDocs: FirestoreDoc<PermissionRequest>[]
): {
  nextLog: FirestoreDoc<PermissionRequest> | undefined;
  additionalEvents: RequestLog[];
} => {
  if (isa(TerminalRequestStatuses, updates.status)) {
    // TODO: simplify more for handling cleanup statuses
    // the current logic is a bit convoluted
    // with cleanup statuses, the current terminal status can be both terminal
    // and non-terminal statuses based on their zombie grants being present
    // so we make sure the history does not contain any cleanup statuses
    // if there are clean up statuses, we continue to the next clean up status
    const isCleanedUp = logDocs.some((doc) =>
      ["CLEANUP_SUBMITTED", "CLEANED_UP", "CLEANUP_ERRORED"].includes(doc.id)
    );
    let nextLog, additionalEvents;

    if (isCleanedUp) {
      nextLog =
        updates.status !== "CLEANED_UP" && updates.status !== "CLEANUP_ERRORED"
          ? logDocs.find((doc) => doc.id === "CLEANUP_SUBMITTED")
          : undefined;
      additionalEvents =
        updates.status === "CLEANED_UP" || updates.status === "CLEANUP_ERRORED"
          ? [{ initialStatus: updates.status, updates }]
          : [];
    } else {
      nextLog = undefined;
      additionalEvents = [{ initialStatus: updates.status, updates }];
    }

    return { nextLog, additionalEvents };
  }

  if (isa(HandledRequestStatuses, updates.status))
    return {
      nextLog: logDocs.find((doc) => doc.id === updates.status),
      additionalEvents: [],
    };

  // TODO: Refactor to make request updates in a driver class in order to log all request updates
  // Currently, logging is done in the state machine handler, so we do not logs for transitions that
  // occur outside of that. This is a hack to deal with that for now.
  switch (updates.status) {
    case "PENDING_APPROVAL": {
      /**
       * In normal flow pending approval is never recorded as a handled request status, as there will be no logs for it
       * we try to find if the request has been approved or denied. if these state exists, we skip over to that status
       * else we consider it as pending_approval and show pending approval state. but with pending approval escalated state
       * pending_approval state can exist. if the request is escalated, there will be a pending_approval status, which we can
       * use to show users that the request has been escalated and pending_approval_escalated state behaves like pending_approval state
       * and moves to approved or denied state
       */
      const escalated = logDocs.find((doc) => doc.id === "PENDING_APPROVAL");
      if (escalated) {
        return {
          nextLog: escalated,
          additionalEvents: [],
        };
      }
      const nextLog = logDocs.find(
        (doc) =>
          doc.id === "APPROVED" || doc.id === "DENIED" || doc.id === "TIMED_OUT"
      );
      return {
        nextLog,
        additionalEvents: nextLog
          ? []
          : [{ initialStatus: "PENDING_APPROVAL", updates }],
      };
    }
    case "PENDING_APPROVAL_ESCALATED": {
      const nextLog = logDocs.find(
        (doc) => doc.id === "APPROVED" || doc.id === "DENIED"
      );
      return {
        nextLog,
        additionalEvents: [
          { initialStatus: "PENDING_APPROVAL_ESCALATED", updates },
        ],
      };
    }
    case "DONE_NOTIFIED":
      return {
        nextLog: logDocs.find(
          (doc) =>
            doc.id === "EXPIRY_SUBMITTED" || doc.id === "REVOKE_SUBMITTED"
        ),
        additionalEvents: [{ initialStatus: "DONE_NOTIFIED", updates }],
      };
    default:
      return { nextLog: undefined, additionalEvents: [] };
  }
};

/**
 * @param request - the permission request document
 * @param requestLogs - the collection of logs attached to request. In general, we expect
 * each log to be named after a given status (e.g. "APPROVED_NOTIFIED") and have an 'initialStatus'
 * field that has the name of the next log in the state-path (e.g. "APPROVED").
 * @returns a boolean indicating if the timeline is complete, and a list of RequestLogs
 * that represents a timeline for the permission-request.
 */
export const getRequestEvents = (
  request: PermissionRequest,
  requestLogs: FirestoreDoc<PermissionRequest>[]
): { events: RequestLog[]; isComplete: boolean } => {
  const requestEvents: RequestLog[] = [];

  // Assume that a "NEW" log exists for each permission request; timeline always starts from here.
  let currentLog = requestLogs.find((doc) => doc.id === "NEW");
  let updates = currentLog?.data;

  while (currentLog) {
    if (isa(RequestStatuses, currentLog.id)) {
      requestEvents.push({
        initialStatus: currentLog.id,
        updates,
      });
    }

    // The updates variable contains details that allow findNextLog to infer events that
    // aren't represented by a separate logs document, but rather by data within
    // a given logs document (e.g. notification details)
    updates = currentLog.data;
    const { nextLog, additionalEvents } = findNextLog(updates, requestLogs);
    requestEvents.push(...additionalEvents);
    currentLog = updates.status != currentLog.id ? nextLog : undefined;
  }

  // Infer if we should mark the timeline as 'complete' or render a spinner
  let isComplete: boolean =
    requestEvents.length > 0 &&
    TerminalRequestStatuses.includes(
      requestEvents[requestEvents.length - 1].initialStatus
    );

  // As a safeguard, we include a sanity check: if the permission request itself seems
  // to have terminated, but the last item of the timeline doesn't seem to, then
  // we mark the timeline as complete and tack on the actual final status.
  if (!isComplete && isa(TerminalRequestStatuses, request.status)) {
    isComplete = true;
    requestEvents.push({
      initialStatus: request.status,
      updates: request, // include the request so that we have an accurate timestamp
    });
  }

  return {
    events: requestEvents,
    isComplete,
  };
};

function extractEscalationDetails(request: PermissionRequest) {
  const { escalationDetails } = request;
  if (!escalationDetails) return undefined;

  // Escalation sources are written generically per-integration; render the
  // first one with any incidents, rather than hard-coding the known sources.
  const entry = Object.entries(escalationDetails).find(
    ([, incidents]) => incidents?.length
  );
  if (!entry) return undefined;

  const [source, incidents] = entry;
  return `and created ${source} ${pluralize(
    "incident",
    incidents.length
  )} ${incidents.join(", ")}`;
}

export const statusToDisplayText = (status: string) => {
  const rs = status as RequestStatus;
  switch (rs) {
    case "APPROVED":
    case "APPROVED_NOTIFIED":
      return "Approved";
    case "CLEANED_UP":
      return "Cleanup Completed";
    case "CLEANUP_ERRORED":
      return "Errored";
    case "CLEANUP_SUBMITTED":
      return "Cleanup in Progress";
    case "EXPIRY_SUBMITTED":
      return "Expiring";
    case "DENIED":
    case "DENIED_NOTIFIED":
      return "Denied";
    case "DONE":
    case "DONE_NOTIFIED":
      return "Granted";
    case "ERRORED":
    case "ERRORED_ERRORED":
    case "ERRORED_NOTIFIED":
      return "Errored";
    case "EXPIRED":
    case "EXPIRED_NOTIFIED":
      return "Expired";
    case "NEW":
      return "Request Created";
    case "PENDING_APPROVAL":
    case "PENDING_APPROVAL_ESCALATED":
      return "Pending Approval";
    case "REVOKE_SUBMITTED":
      return "Relinquishing";
    case "REVOKED":
    case "REVOKED_NOTIFIED":
      return "Relinquished";
    case "STAGED":
      return "Staged";
    case "DRAFT":
    case "TRANSLATED":
      return "N/A";
    case "TIMED_OUT":
    case "TIMED_OUT_NOTIFIED":
      return "Timed Out";
    case undefined:
    default: {
      const _validateExhaustion: never = rs;
      return UNKNOWN;
    }
  }
};

export const deserializeQueryParamValue = (value: string | null) => {
  if (value === "null") {
    return null;
  } else if (value === "true") {
    return true;
  } else if (value === "false") {
    return false;
  } else if (!Number.isNaN(Number(value))) {
    return Number(value);
  } else if (!!value) {
    return value;
  } else {
    return null;
  }
};

export const serializeQueryParamValue = (
  value: boolean | number | string | null
): string | null => {
  if (value === null) {
    return null;
  } else if (value === true) {
    return "true";
  } else if (value === false) {
    return "false";
  } else if (typeof value === "number") {
    if (Number.isNaN(value)) {
      return null;
    }
    return value.toString();
  } else if (!!value) {
    return value;
  }
  return null;
};

export const getPreApprovalData = (
  request: PermissionRequest
): readonly [Evidence["source"], string] | "unknown" | undefined => {
  const { approvalDetails } = request;
  const { approvalSource } = approvalDetails ?? {};

  if (approvalSource !== "evidence") {
    return undefined;
  }

  const { evidence: evidenceList } = request;

  if (!evidenceList || evidenceList.length === 0) {
    return "unknown";
  }

  const [evidence] = evidenceList;
  const { source } = evidence;

  switch (source) {
    case "request":
      return ["request", evidence.requestId] as const;
    case "allow":
      return ["allow", evidence.allowId] as const;
    default:
      assertNever(source);
  }
};

export const handleRequestActionResponse = (
  response: Response | undefined,
  success: string
) => {
  if (response?.ok) {
    message.success(success);
  }
};

/** Whether the viewing user is the request's principal — i.e. the grant holder.
 *
 * Requests can be filed on behalf of another principal (see `RequestPreview`),
 * so this keys off `principal` (who holds the access), not `requestor` (who filed
 * it). Distinguishes "Relinquish" (the viewer holds the grant themselves, so
 * re-requesting is meaningless) from "Revoke" (someone else holds the grant, and
 * the viewer may still make an equivalent request of their own).
 *
 * `principal` is usually an email but can be a uid for some integrations (e.g.
 * SSH), so both are matched — mirroring the backend's own principal checks.
 */
export const isGrantHolder = (
  user: { email?: string | null; uid?: string | null } | undefined,
  request: Pick<PermissionRequest, "principal"> | undefined
) =>
  !!request?.principal &&
  (user?.email === request.principal || user?.uid === request.principal);

export const toRequestDescription = (request: PermissionRequest) =>
  requestDescription(request)?.replace(/.*\| /, "");

export const toRequestType = ({ type }: PermissionRequest) =>
  isPermissionType(type) ? integrationRegistry[type].label : UNKNOWN;

export const toRequestLocation = (
  request: PermissionRequest,
  flags: Record<string, boolean>
) => {
  const location = requestLocator(request);
  const integrationMap = [
    ...cloneDeep(resourceIntegrations(flags)),
    ...cloneDeep(directories(flags)),
  ];

  return location
    ? String(location)
    : integrationMap.find((i) => i.key === request.type)?.label;
};
export const toRequestAccessType = (request: PermissionRequest) =>
  "access" in request ? startCase(request.access) : "";
